import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import {
    getThirdPartyApiCorporateConfig,
    getThirdPartyApiKeys,
    IThirdPartyApiKey,
    revokeThirdPartyApiKey,
} from "api/queries/apiKeysAPI";

// Mirrors the DB default for `third_party_api_corporate_config.max_active_api_keys` — covers a
// missing config row, matching the backend's fallback in ThirdPartyApiKeyEntity (OS-2552).
export const DEFAULT_MAX_ACTIVE_API_KEYS = 5;

/**
 * House entity pattern (mirror of CorporationsEntity / EventReportsEntity): components call these
 * methods and never touch Apollo hooks directly. Reads are row-scoped by Hasura permissions to the
 * caller's own Corporation, so no corporate_id variable is passed. Query results are captured in
 * `onCompleted` (not a data-watching useEffect).
 */
export const ApiKeysEntity = {
    GetThirdPartyApiKeys: (skip?: boolean, activeOnly?: boolean) => {
        const [apiKeys, setApiKeys] = useState<IThirdPartyApiKey[]>([]);

        // The "not yet expired" boundary is recomputed per refresh (tick bump) and per filter
        // toggle — NOT per render (a fresh value each render would loop Apollo into refetching).
        // That keeps the cut-off honest for short custom expiries: every mutation-triggered
        // refresh re-evaluates expiry against the current time, so the list can't disagree with
        // the live active-key count all session. Same active definition as the MBO aggregate:
        // not revoked AND not yet expired (contract §5.2.1 / thirdPartyApiAccessAPI).
        const [refreshTick, setRefreshTick] = useState(0);
        // refreshTick/activeOnly are the deliberate recompute triggers
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const now = useMemo(() => new Date().toISOString(), [refreshTick, activeOnly]);
        const where = useMemo(
            () => (activeOnly ? { expires_at: { _gt: now }, revoked_at: { _is_null: true } } : {}),
            [activeOnly, now],
        );

        // no-cache mirrors the MBO sibling (ThirdPartyApiAccessEntity) and the house norm;
        // notifyOnNetworkStatusChange makes onCompleted re-fire on refetch (Apollo 3.7 skips
        // it otherwise), so the table reflects generate/rotate/revoke without a remount.
        // Flipping `activeOnly` changes `where`, which re-runs the query at the source.
        const { error, loading, refetch: apolloRefetch } = useQuery(getThirdPartyApiKeys, {
            fetchPolicy: "no-cache",
            notifyOnNetworkStatusChange: true,
            skip,
            variables: { where },
            onCompleted: (data) => {
                if (data?.third_party_api_key) {
                    setApiKeys(data.third_party_api_key);
                }
            },
        });

        // Stable refresh entry point for consumers (the view calls it after generate/rotate/
        // revoke and from Retry). Bumping the tick recomputes `now`; with the filter ON the
        // resulting variable change re-runs the no-cache query by itself (also calling Apollo's
        // refetch would double-fire), while with the filter OFF `where` stays deep-equal ({}),
        // so Apollo's refetch must be invoked explicitly. Failures keep flowing to
        // `apolloQueryError` either way; the explicit refetch's rejection is the caller's to
        // catch (the view already does).
        const refetch = useCallback((): Promise<unknown> => {
            setRefreshTick((tick) => tick + 1);
            return activeOnly ? Promise.resolve() : apolloRefetch();
        }, [activeOnly, apolloRefetch]);

        return { apiKeys, apolloQueryError: error, loading, refetch };
    },
    GetThirdPartyApiCorporateConfig: (skip?: boolean) => {
        const [isEnabled, setIsEnabled] = useState<boolean>(false);
        const [maxActiveApiKeys, setMaxActiveApiKeys] = useState<number>(DEFAULT_MAX_ACTIVE_API_KEYS);

        const { loading } = useQuery(getThirdPartyApiCorporateConfig, {
            fetchPolicy: "no-cache",
            skip,
            onCompleted: (data) => {
                // Missing config row (empty array) reads as disabled — never accidentally open (AC-1).
                const config = data?.third_party_api_corporate_config?.[0];
                setIsEnabled(Boolean(config?.is_enabled));
                // The cap is DB-driven (D-2552-3); a missing row falls back to the shared default.
                setMaxActiveApiKeys(config?.max_active_api_keys ?? DEFAULT_MAX_ACTIVE_API_KEYS);
            },
        });

        return { isEnabled, loading, maxActiveApiKeys };
    },
    RevokeThirdPartyApiKey: () => {
        const [revokeKey, { error, loading }] = useMutation(revokeThirdPartyApiKey);
        return { apolloMutationError: error, revokeKey, revoking: loading };
    },
};
